import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../../api/Agent'
import {IShippingAndTax, ShippingAndTaxFormValues, ShippingAndTaxReturnFormValues} from '../../models/ShippingAndTax/ShippingAndTax'
import {toast} from 'react-toastify'
import {RootStore} from '../RootStore'


export default class ShippingAndTaxStore {
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)

    reaction(
      () => this.predicate.keys(),
      () => {
        this.page = 0
        this.shippingAndTaxesRegistry.clear()
        this.loadShippingAndTax()
      }
    )
  }

  shippingAndTaxesRegistry = new Map()
  shippingAndTaxes: IShippingAndTax | null = null
  editMode = false

  shippingAndTaxesCount = 0
  page = 0
  limit = 5

  predicate = new Map()

  setPredicate = (predicate?: string, value?: any) => {
    if (!predicate || predicate === '') {
      this.predicate.clear()
    } else {
      this.predicate.delete(predicate)
      if (!!value)
        this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
    }
  }

  setPredicates = (predicates: {}) => {
    this.shippingAndTaxesRegistry.clear()
    let tmpArr = Object.entries(predicates)
    this.predicate = new Map(tmpArr)
  }

  get axiosParams() {
    const params = new URLSearchParams()
    params.append('limit', String(this.limit))
    params.append('offset', `${this.page ? this.page * this.limit : 0}`)
    this.predicate.forEach((value, key) => {
      if (!(value === '' || value === undefined || value === 'undefined') && typeof value !== 'object'){
        params.append(key, value)
      } else if (typeof value === 'object') {
        value?.forEach(val => params.append(key, val))
      }
    })
    return params
  }

  get totalPages() {
    return Math.ceil(this.shippingAndTaxesCount / this.limit)
  }

  get shippingAndTaxesList() {
    return Array.from(this.shippingAndTaxesRegistry.values())
    // .sort((a,b) => b.updatedOn - a.updatedOn)
  }

  get shippingAndTaxesByDate() {
    return Array.from(this.shippingAndTaxesRegistry.values())
    // .sort(
    //     (a, b) => b.dateCreated - a.dateCreated//   Date.parse(a.SubmittedOn)-Date.parse(b.SubmittedOn)
    // )
  }

  getShippingAndTax = (id: string) => {
    return this.shippingAndTaxesRegistry.get(id)
  }

  setPage = (page: number) => {
    this.page = page
  }

  setLimit = (limit: number) => {
    this.limit = limit
  }

  handleChangePage = (newPage: number, newRow?: number) => {
    //sets page, we do -1 because the offset will be multiplied against 
    this.setPage(newPage - 1)
    //sets the row limit
    if (!!newRow) this.setLimit(newRow)
    //clears all records in the registry so that next page didnt load the prev records
    this.shippingAndTaxesRegistry.clear()
    this.loadShippingAndTax()
  }

  clearShippingAndTax = () => {
    this.shippingAndTaxesRegistry.clear()
  }

  loadShippingAndTax = async () => {
    'loading'
    let loadingId = this.rootStore.commonStore.setIsLoading()
    try {
      const shippingAndTaxesEnvelope = await Agent.ShippingAndTaxes.list(this.axiosParams)
      const {modelsDto, modelCount} = shippingAndTaxesEnvelope

      runInAction( () => {
        this.clearShippingAndTax()
        modelsDto.forEach((shippingAndTaxes: IShippingAndTax) => {
          this.shippingAndTaxesRegistry.set(shippingAndTaxes.id, shippingAndTaxes)
        })
        this.shippingAndTaxesCount = modelCount
      })
    } catch (error) {
      throw error
    } finally {
      runInAction( () => {
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
    }
  }

  deleteShippingAndTax = async (shippingAndTax: IShippingAndTax) => {
    let loadingId = this.rootStore.commonStore.setIsLoading()
    try {
      if (!shippingAndTax.id) throw  new Error('Shipping and Tax Id required to delete record')
      await Agent.ShippingAndTaxes.delete(shippingAndTax.id)
      runInAction( () => {
        this.shippingAndTaxesRegistry.delete(shippingAndTax.id)
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
      toast.success('Saved')
    } catch (error) {
      this.rootStore.errorStore.addError(error)
    } finally {
      runInAction(() => {
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
    }
  }

  getShippingAndTaxById = async (id: string) => {
    let loadingId = this.rootStore.commonStore.setIsLoading()
    try {
      let shippingAndTaxes = await Agent.ShippingAndTaxes.byId(id)
      runInAction( () => {
        this.shippingAndTaxes = shippingAndTaxes
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
      return shippingAndTaxes
    } catch (error) {
      this.rootStore.errorStore.addError(error)
    } finally {
      runInAction(() => {
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
    }
  }

  createShippingAndTax = async (shippingAndTaxes: IShippingAndTax) => {
    let loadingId = this.rootStore.commonStore.setIsLoading()
    try {
      const newShippingAndTax = await Agent.ShippingAndTaxes.create(shippingAndTaxes)
      await this.loadShippingAndTax()
      runInAction( () => {
        this.shippingAndTaxesRegistry.set(newShippingAndTax.id, newShippingAndTax)
        this.rootStore.modalStore.closeModal('ShippingAndTaxModal')
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
      toast.success('Saved')
      return newShippingAndTax
    } catch (error) {
      this.rootStore.errorStore.addError(error)
    } finally {
      runInAction( () => {
        this.rootStore.commonStore.setIsLoading(loadingId)
      })
    }
  }

  editShippingAndTax = async (shippingAndTaxes: ShippingAndTaxFormValues) => {
    let loadingId = this.rootStore.commonStore.setIsLoading()
    try {
      let updatedShippingAndTax = await Agent.ShippingAndTaxes.update(shippingAndTaxes)
      runInAction( () => {
        this.shippingAndTaxesRegistry.set(updatedShippingAndTax.id, updatedShippingAndTax)
        this.rootStore.modalStore.closeModal('ShippingAndTaxModal')
      })
      toast.success('Saved')
      return updatedShippingAndTax
    } catch (error) {
      this.rootStore.errorStore.addError(error)
    } finally {
      this.rootStore.commonStore.setIsLoading(loadingId)
    }
  }

  returnShippingAndTax = async (shippingAndTaxReturn: ShippingAndTaxReturnFormValues) => {
    let loadingId = this.rootStore.commonStore.setIsLoading()
    try {
      let returnShippingAndTax = await Agent.ShippingAndTaxes.return(shippingAndTaxReturn)
      runInAction(async () => {
        this.rootStore.modalStore.closeModal('ShippingTaxReturnModal')
      })
      toast.success('Saved')
      return returnShippingAndTax
    } catch (error) {
      this.rootStore.errorStore.addError(error)
    } finally {
      this.rootStore.commonStore.setIsLoading(loadingId)
    }
  }
}
